import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Routes, Route, NavLink, Navigate } from "react-router-dom";
import {
  MasterCrudPage,
  useRouteModulePermissions,
} from "@/components/common/Crud";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Plus, Search, Pencil, Trash2, Eye, Download } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  LibraryBooksModule,
  LibraryMembersModule,
  LibraryIssueReturnModule,
  LibraryCardBlueprint,
} from "@/pages/LibraryModule";
import { DataTable, PageTitle } from "@/components/common/Crud";
import { DatePicker } from "@/components/common/DatePicker";
import { MultiCheck } from "@/components/common/MultiCheck";
import { BACKEND_URL } from "@/lib/api";

function SearchableSelect({
  label,
  items,
  value,
  onChange,
  hideLabel = false,
  placeholder = "Search...",
}) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);

  const selected = useMemo(
    () => (items || []).find((x) => String(x.id) === String(value)) || null,
    [items, value],
  );

  useEffect(() => {
    if (selected && q.trim() === "") {
      setQ(selected.name || "");
    }
  }, [selected, q]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const rows = items || [];
    if (!needle) return rows.slice(0, 30);
    return rows
      .filter((x) => {
        const hay = [x.name, x.subtitle, x.keywords]
          .map((v) => String(v || "").toLowerCase())
          .join(" ");
        return hay.includes(needle);
      })
      .slice(0, 30);
  }, [items, q]);

  return (
    <div className="relative">
      {!hideLabel && label ? (
        <Label className="text-xs font-semibold mb-1 block">{label}</Label>
      ) : null}
      <Input
        value={q}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 160)}
        onChange={(e) => {
          setQ(e.target.value);
          setOpen(true);
          if (!e.target.value.trim() && value) onChange("");
        }}
        placeholder={placeholder}
      />
      {open && (
        <div className="absolute z-50 mt-1 w-full rounded-md border border-slate-200 bg-white shadow-lg max-h-56 overflow-auto">
          {filtered.length === 0 && (
            <div className="px-3 py-2 text-sm text-slate-400">
              No matching records
            </div>
          )}
          {filtered.map((x) => (
            <button
              key={x.id}
              type="button"
              className="w-full text-left px-3 py-2 hover:bg-amber-50 border-b last:border-b-0"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                onChange(String(x.id));
                setQ(x.name || "");
                setOpen(false);
              }}
            >
              <div className="text-sm font-medium text-slate-800">{x.name}</div>
              {x.subtitle ? (
                <div className="text-xs text-slate-500">{x.subtitle}</div>
              ) : null}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function LibraryFinesModule() {
  return (
    <MasterCrudPage
      title="Library Fines"
      endpoint="/library-fines"
      testid="fines"
      fields={[
        { key: "member_id", label: "Member ID" },
        { key: "book_issue_id", label: "Issue ID" },
        { key: "amount", label: "Amount", type: "number" },
        { key: "reason", label: "Reason" },
        { key: "date", label: "Date", type: "date" },
        {
          key: "paid",
          label: "Paid",
          type: "select",
          options: [
            { value: "0", label: "Due" },
            { value: "1", label: "Paid" },
          ],
        },
      ]}
    />
  );
}

function InventoryItemsModule() {
  const { branchId } = useAuth();
  const permissions = useRouteModulePermissions("inventory", "items");
  const DEFAULT_UNITS = [
    "PCS",
    "BOX",
    "PACK",
    "KG",
    "GM",
    "LTR",
    "ML",
    "METER",
    "DOZEN",
    "SET",
  ];
  const [rows, setRows] = useState([]);
  const [categories, setCategories] = useState([]);
  const [stores, setStores] = useState([]);
  const [stockRows, setStockRows] = useState([]);
  const [issueRows, setIssueRows] = useState([]);
  const [unitOptions, setUnitOptions] = useState(DEFAULT_UNITS);
  const [newUnit, setNewUnit] = useState("");
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteRow, setDeleteRow] = useState(null);
  const [importing, setImporting] = useState(false);
  const importRef = useRef(null);
  const [form, setForm] = useState({
    name: "",
    category_id: "",
    store_id: "",
    unit: "PCS",
    unit_price: "0",
    min_qty: "0",
    qty: "0",
  });

  const load = async () => {
    try {
      const [itemsRes, catRes, storeRes, stockRes, issueRes] =
        await Promise.all([
          api.get("/inventory-items", {
            params: { branch_id: branchId, limit: 1000 },
          }),
          api.get("/item-categories", {
            params: { branch_id: branchId, limit: 1000 },
          }),
          api.get("/item-stores", {
            params: { branch_id: branchId, limit: 1000 },
          }),
          api.get("/item-stock", {
            params: { branch_id: branchId, limit: 5000 },
          }),
          api.get("/item-issues", {
            params: { branch_id: branchId, limit: 5000 },
          }),
        ]);
      const itemRows = itemsRes.data.items || [];
      setRows(itemRows);
      setCategories(catRes.data.items || []);
      setStores(storeRes.data.items || []);
      setStockRows(stockRes.data.items || []);
      setIssueRows(issueRes.data.items || []);

      const dynamicUnits = itemRows
        .map((r) => String(r.unit || "").trim())
        .filter(Boolean)
        .map((u) => u.toUpperCase());
      setUnitOptions(Array.from(new Set([...DEFAULT_UNITS, ...dynamicUnits])));
    } catch {
      toast.error("Failed to load inventory items");
      setRows([]);
    }
  };

  useEffect(() => {
    load();
  }, [branchId]);

  const categoryMap = useMemo(
    () => new Map((categories || []).map((c) => [String(c.id), c.name])),
    [categories],
  );
  const storeMap = useMemo(
    () => new Map((stores || []).map((s) => [String(s.id), s.name])),
    [stores],
  );

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (rows || []).filter((r) => {
      if (!q) return true;
      const hay = [
        r.name,
        categoryMap.get(String(r.category_id || "")),
        storeMap.get(String(r.store_id || "")),
        r.code,
      ]
        .map((x) => String(x || "").toLowerCase())
        .join(" ");
      return hay.includes(q);
    });
  }, [rows, search, categoryMap, storeMap]);

  const currentStockMap = useMemo(() => {
    const inMap = new Map();
    const outMap = new Map();

    (stockRows || []).forEach((s) => {
      const key = String(s.item_id || "");
      const qty = Number(s.qty || 0);
      inMap.set(key, (inMap.get(key) || 0) + (Number.isFinite(qty) ? qty : 0));
    });

    (issueRows || []).forEach((s) => {
      const key = String(s.item_id || "");
      const qty = Number(s.qty || 0);
      outMap.set(
        key,
        (outMap.get(key) || 0) + (Number.isFinite(qty) ? qty : 0),
      );
    });

    const out = new Map();
    (rows || []).forEach((r) => {
      const key = String(r.id || "");
      if (!key) return;
      const purchased = inMap.get(key) || 0;
      const issued = outMap.get(key) || 0;
      const movementBased = purchased - issued;
      if (Number.isFinite(Number(r.current_stock))) {
        out.set(key, Number(r.current_stock));
      } else if (purchased !== 0 || issued !== 0) {
        out.set(key, movementBased);
      } else {
        out.set(key, Number(r.qty || 0));
      }
    });
    return out;
  }, [rows, stockRows, issueRows]);

  const openCreate = () => {
    if (!permissions.canAdd)
      return toast.error("You do not have add permission for inventory items");
    setEditing(null);
    setForm({
      name: "",
      category_id: "",
      store_id: "",
      unit: "PCS",
      unit_price: "0",
      min_qty: "0",
      qty: "0",
    });
    setNewUnit("");
    setOpen(true);
  };

  const openEdit = (row) => {
    if (!permissions.canEdit)
      return toast.error("You do not have edit permission for inventory items");
    setEditing(row);
    setForm({
      name: String(row.name || ""),
      category_id: String(row.category_id || ""),
      store_id: String(row.store_id || ""),
      unit: String(row.unit || "PCS"),
      unit_price: String(row.unit_price ?? row.rate ?? 0),
      min_qty: String(row.min_qty ?? 0),
      qty: String(row.qty ?? 0),
    });
    setNewUnit("");
    setOpen(true);
  };

  const save = async () => {
    if (editing && !permissions.canEdit)
      return toast.error("You do not have edit permission for inventory items");
    if (!editing && !permissions.canAdd)
      return toast.error("You do not have add permission for inventory items");
    if (!String(form.name || "").trim())
      return toast.error("Item name is required");

    const payload = {
      name: String(form.name || "").trim(),
      category_id: form.category_id ? Number(form.category_id) : undefined,
      store_id: form.store_id ? Number(form.store_id) : undefined,
      unit: String(form.unit || "PCS")
        .trim()
        .toUpperCase(),
      unit_price: Number(form.unit_price || 0),
      rate: Number(form.unit_price || 0),
      min_qty: Number(form.min_qty || 0),
      qty: Number(form.qty || 0),
      branch_id: branchId,
    };

    try {
      if (editing) {
        await api.put(`/inventory-items/${editing.id}`, payload);
        toast.success("Item updated");
      } else {
        await api.post("/inventory-items", payload);
        toast.success("Item created");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const del = async () => {
    if (!deleteRow?.id) return;
    if (!permissions.canDelete)
      return toast.error(
        "You do not have delete permission for inventory items",
      );
    try {
      await api.delete(`/inventory-items/${deleteRow.id}`);
      toast.success("Item deleted");
      setDeleteRow(null);
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const parseCsvLine = (line) => {
    const out = [];
    let cur = "";
    let quoted = false;
    for (let i = 0; i < line.length; i += 1) {
      const ch = line[i];
      if (ch === '"') {
        if (quoted && line[i + 1] === '"') {
          cur += '"';
          i += 1;
        } else {
          quoted = !quoted;
        }
      } else if (ch === "," && !quoted) {
        out.push(cur.trim());
        cur = "";
      } else {
        cur += ch;
      }
    }
    out.push(cur.trim());
    return out;
  };

  const onImportCsv = async (event) => {
    if (!permissions.canAdd) {
      toast.error("You do not have add permission for inventory items");
      return;
    }
    const file = event.target.files?.[0];
    if (!file) return;

    setImporting(true);
    try {
      const text = await file.text();
      const lines = text
        .split(/\r?\n/)
        .map((l) => l.trim())
        .filter(Boolean);
      if (lines.length < 2) {
        toast.error("CSV must include header and at least one row");
        return;
      }

      const headers = parseCsvLine(lines[0]).map((h) => h.toLowerCase());
      const getIdx = (name) => headers.indexOf(name);
      const idxName = getIdx("name");
      const idxCategory = getIdx("category");
      const idxStore = getIdx("store");
      const idxPrice = getIdx("unit_price");
      const idxMin = getIdx("min_qty");
      const idxQty = getIdx("qty");
      const idxUnit = getIdx("unit");
      if (idxName < 0) {
        toast.error("CSV header must include: name");
        return;
      }

      let imported = 0;
      for (let i = 1; i < lines.length; i += 1) {
        const cols = parseCsvLine(lines[i]);
        const name = String(cols[idxName] || "").trim();
        if (!name) continue;

        const categoryName =
          idxCategory >= 0
            ? String(cols[idxCategory] || "")
                .trim()
                .toLowerCase()
            : "";
        const storeName =
          idxStore >= 0
            ? String(cols[idxStore] || "")
                .trim()
                .toLowerCase()
            : "";
        const category = (categories || []).find(
          (c) =>
            String(c.name || "")
              .trim()
              .toLowerCase() === categoryName,
        );
        const store = (stores || []).find(
          (s) =>
            String(s.name || "")
              .trim()
              .toLowerCase() === storeName,
        );
        const unitPrice = idxPrice >= 0 ? Number(cols[idxPrice] || 0) : 0;
        const minQty = idxMin >= 0 ? Number(cols[idxMin] || 0) : 0;
        const qty = idxQty >= 0 ? Number(cols[idxQty] || 0) : 0;
        const unit =
          idxUnit >= 0
            ? String(cols[idxUnit] || "PCS")
                .trim()
                .toUpperCase() || "PCS"
            : "PCS";

        await api.post("/inventory-items", {
          name,
          category_id: category?.id,
          store_id: store?.id,
          unit,
          unit_price: Number.isFinite(unitPrice) ? unitPrice : 0,
          rate: Number.isFinite(unitPrice) ? unitPrice : 0,
          min_qty: Number.isFinite(minQty) ? minQty : 0,
          qty: Number.isFinite(qty) ? qty : 0,
          branch_id: branchId,
        });
        imported += 1;
      }

      toast.success(`Imported ${imported} items`);
      load();
    } catch {
      toast.error("CSV import failed");
    } finally {
      setImporting(false);
      if (importRef.current) importRef.current.value = "";
    }
  };

  const downloadSampleCsv = () => {
    const sample = [
      "name,category,store,unit,unit_price,min_qty,qty",
      "Pen,Stationary,School,PCS,10,10,100",
      "Notebook,Stationary,Main Store,PCS,45,20,250",
    ].join("\n");
    const blob = new Blob([sample], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "inventory_items_sample.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const addUnitInstant = () => {
    const normalized = String(newUnit || "")
      .trim()
      .toUpperCase();
    if (!normalized) return;
    setUnitOptions((prev) =>
      prev.includes(normalized) ? prev : [...prev, normalized],
    );
    setForm((f) => ({ ...f, unit: normalized }));
    setNewUnit("");
  };

  return (
    <div className="space-y-3" data-testid="inventory-items-custom">
      <div className="border border-slate-200 bg-white rounded-md p-3 shadow-sm flex flex-wrap gap-2 items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-800">
          Inventory Items
        </h3>
        <div className="flex gap-2 w-full md:w-auto">
          <div className="relative flex-1 md:w-72">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search item / category / store"
              className="pl-9"
            />
          </div>
          <Button
            variant="outline"
            onClick={() => importRef.current?.click()}
            disabled={!permissions.canAdd || importing}
          >
            {importing ? "Importing..." : "Import CSV"}
          </Button>
          <Button variant="outline" onClick={downloadSampleCsv}>
            Sample CSV
          </Button>
          <input
            ref={importRef}
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            onChange={onImportCsv}
          />
          <Button
            className="erp-primary"
            onClick={openCreate}
            disabled={!permissions.canAdd}
          >
            <Plus className="w-4 h-4 mr-1" />
            Add New
          </Button>
        </div>
      </div>

      <div className="border border-slate-200 bg-white rounded-md overflow-hidden shadow-sm">
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr>
              <th className="text-left px-3 py-2">Item Name</th>
              <th className="text-left px-3 py-2">Category</th>
              <th className="text-left px-3 py-2">Store</th>
              <th className="text-left px-3 py-2">Unit</th>
              <th className="text-left px-3 py-2">Unit Price</th>
              <th className="text-left px-3 py-2">Min Qty</th>
              <th className="text-left px-3 py-2">Quantity</th>
              <th className="text-left px-3 py-2">Current Stock</th>
              {(permissions.canEdit || permissions.canDelete) && (
                <th className="text-left px-3 py-2">Actions</th>
              )}
            </tr>
          </thead>
          <tbody>
            {filteredRows.map((row) => (
              <tr key={row.id} className="border-t">
                <td className="px-3 py-2">{row.name || "-"}</td>
                <td className="px-3 py-2">
                  {categoryMap.get(String(row.category_id || "")) || "-"}
                </td>
                <td className="px-3 py-2">
                  {storeMap.get(String(row.store_id || "")) || "-"}
                </td>
                <td className="px-3 py-2">{row.unit || "PCS"}</td>
                <td className="px-3 py-2">{row.unit_price ?? row.rate ?? 0}</td>
                <td className="px-3 py-2">{row.min_qty ?? 0}</td>
                <td className="px-3 py-2">{row.qty ?? 0}</td>
                <td className="px-3 py-2">
                  {currentStockMap.get(String(row.id || "")) ?? 0}
                </td>
                {(permissions.canEdit || permissions.canDelete) && (
                  <td className="px-3 py-2">
                    <div className="flex gap-1">
                      {permissions.canEdit && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openEdit(row)}
                        >
                          <Pencil className="w-3.5 h-3.5 mr-1" />
                          Edit
                        </Button>
                      )}
                      {permissions.canDelete && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setDeleteRow(row)}
                        >
                          <Trash2 className="w-3.5 h-3.5 mr-1" />
                          Delete
                        </Button>
                      )}
                    </div>
                  </td>
                )}
              </tr>
            ))}
            {filteredRows.length === 0 && (
              <tr>
                <td
                  colSpan={permissions.canEdit || permissions.canDelete ? 9 : 8}
                  className="px-3 py-6 text-center text-slate-400"
                >
                  No items found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>
              {editing ? "Edit Inventory Item" : "Add New · Inventory Item"}
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-semibold mb-1 block">
                Item Name *
              </Label>
              <Input
                value={form.name}
                onChange={(e) =>
                  setForm((f) => ({ ...f, name: e.target.value }))
                }
              />
            </div>
            <SearchableSelect
              label="Category"
              value={form.category_id}
              onChange={(v) => setForm((f) => ({ ...f, category_id: v }))}
              items={(categories || []).map((c) => ({
                id: c.id,
                name: c.name || `Category ${c.id}`,
              }))}
              placeholder="Search category"
            />
            <SearchableSelect
              label="Store"
              value={form.store_id}
              onChange={(v) => setForm((f) => ({ ...f, store_id: v }))}
              items={(stores || []).map((s) => ({
                id: s.id,
                name: s.name || `Store ${s.id}`,
              }))}
              placeholder="Search store"
            />
            <div>
              <Label className="text-xs font-semibold mb-1 block">Unit</Label>
              <select
                className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                value={form.unit}
                onChange={(e) =>
                  setForm((f) => ({ ...f, unit: e.target.value }))
                }
              >
                {unitOptions.map((u) => (
                  <option key={u} value={u}>
                    {u}
                  </option>
                ))}
              </select>
              <div className="mt-2 flex gap-2">
                <Input
                  value={newUnit}
                  onChange={(e) => setNewUnit(e.target.value)}
                  placeholder="Add new unit (instant)"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={addUnitInstant}
                >
                  Add
                </Button>
              </div>
            </div>
            <div>
              <Label className="text-xs font-semibold mb-1 block">
                Unit Price
              </Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={form.unit_price}
                onChange={(e) =>
                  setForm((f) => ({ ...f, unit_price: e.target.value }))
                }
              />
            </div>
            <div>
              <Label className="text-xs font-semibold mb-1 block">
                Min Qty
              </Label>
              <Input
                type="number"
                min="0"
                value={form.min_qty}
                onChange={(e) =>
                  setForm((f) => ({ ...f, min_qty: e.target.value }))
                }
              />
            </div>
            <div>
              <Label className="text-xs font-semibold mb-1 block">
                Quantity
              </Label>
              <Input
                type="number"
                min="0"
                value={form.qty}
                onChange={(e) =>
                  setForm((f) => ({ ...f, qty: e.target.value }))
                }
              />
            </div>
          </div>
          <div className="mt-2 flex gap-2 justify-end">
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button className="erp-primary" onClick={save}>
              {editing ? "Update" : "Create"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={!!deleteRow}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) setDeleteRow(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete inventory item?</AlertDialogTitle>
            <AlertDialogDescription>
              This item will be permanently removed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeleteRow(null)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={del}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function InventoryStockModule() {
  const { branchId, sessionId, user } = useAuth();
  const permissions = useRouteModulePermissions("inventory", "stock");
  const [rows, setRows] = useState([]);
  const [issueRows, setIssueRows] = useState([]);
  const [items, setItems] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [deleteRow, setDeleteRow] = useState(null);
  const [form, setForm] = useState({
    purchase_date: new Date().toISOString().slice(0, 10),
    supplier_id: "",
    invoice_no: "",
    remarks: "",
    lines: [{ item_id: "", qty: "1", rate: "0" }],
  });

  const load = async () => {
    try {
      const [stockRes, issueRes, itemsRes, supRes, usersRes] =
        await Promise.all([
          api.get("/item-stock", {
            params: { branch_id: branchId, limit: 5000 },
          }),
          api.get("/item-issues", {
            params: { branch_id: branchId, limit: 5000 },
          }),
          api.get("/inventory-items", {
            params: { branch_id: branchId, limit: 5000 },
          }),
          api.get("/item-suppliers", {
            params: { branch_id: branchId, limit: 1000 },
          }),
          api.get("/auth/users", {
            params: { branch_id: branchId, session_id: sessionId, limit: 1000 },
          }),
        ]);
      setRows(stockRes.data.items || []);
      setIssueRows(issueRes.data.items || []);
      setItems(itemsRes.data.items || []);
      setSuppliers(supRes.data.items || []);
      setUsers(usersRes.data.items || []);
    } catch {
      toast.error("Failed to load stock records");
      setRows([]);
    }
  };

  useEffect(() => {
    load();
  }, [branchId, sessionId]);

  const itemMap = useMemo(
    () => new Map((items || []).map((i) => [String(i.id), i])),
    [items],
  );
  const supplierMap = useMemo(
    () => new Map((suppliers || []).map((s) => [String(s.id), s.name])),
    [suppliers],
  );
  const userMap = useMemo(
    () =>
      new Map(
        (users || []).map((u) => [
          String(u.id),
          u.name || u.full_name || u.email || `User ${u.id}`,
        ]),
      ),
    [users],
  );

  const resolveEnteredBy = useCallback(
    (row) => {
      const id = String(row.created_by || row.updated_by || "");
      if (!id) return "-";
      if (userMap.has(id)) return userMap.get(id);
      if (String(user?.id || "") === id)
        return user?.name || user?.email || "You";
      return `User ${id}`;
    },
    [userMap, user],
  );

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (rows || []).filter((r) => {
      if (!q) return true;
      const item = itemMap.get(String(r.item_id || ""));
      const hay = [
        item?.name,
        supplierMap.get(String(r.supplier_id || "")),
        r.invoice_no,
        r.purchase_date,
        r.remarks,
        r.remark,
        resolveEnteredBy(r),
      ]
        .map((x) => String(x || "").toLowerCase())
        .join(" ");
      return hay.includes(q);
    });
  }, [rows, search, itemMap, supplierMap, resolveEnteredBy]);

  const resetForm = () => {
    setForm({
      purchase_date: new Date().toISOString().slice(0, 10),
      supplier_id: "",
      invoice_no: "",
      remarks: "",
      lines: [{ item_id: "", qty: "1", rate: "0" }],
    });
  };

  const openCreate = () => {
    if (!permissions.canAdd)
      return toast.error("You do not have add permission for stock");
    setEditingRow(null);
    resetForm();
    setOpen(true);
  };

  const openEdit = (row) => {
    if (!permissions.canEdit)
      return toast.error("You do not have edit permission for stock");
    setEditingRow(row);
    setForm({
      purchase_date: String(
        row.purchase_date || new Date().toISOString().slice(0, 10),
      ),
      supplier_id: String(row.supplier_id || ""),
      invoice_no: String(row.invoice_no || ""),
      remarks: String(row.remarks || row.remark || row.note || ""),
      lines: [
        {
          item_id: String(row.item_id || ""),
          qty: String(row.qty ?? 0),
          rate: String(row.rate ?? 0),
        },
      ],
    });
    setOpen(true);
  };

  const addLine = () => {
    setForm((f) => ({
      ...f,
      lines: [...f.lines, { item_id: "", qty: "1", rate: "0" }],
    }));
  };

  const removeLine = (idx) => {
    setForm((f) => ({
      ...f,
      lines: f.lines.filter((_, i) => i !== idx),
    }));
  };

  const setLine = (idx, patch) => {
    setForm((f) => ({
      ...f,
      lines: f.lines.map((ln, i) => (i === idx ? { ...ln, ...patch } : ln)),
    }));
  };

  const save = async () => {
    if (editingRow && !permissions.canEdit)
      return toast.error("You do not have edit permission for stock");
    if (!editingRow && !permissions.canAdd)
      return toast.error("You do not have add permission for stock");

    if (!form.supplier_id) {
      return toast.error("Supplier is required");
    }

    const invoiceTrimmed = String(form.invoice_no || "").trim();
    if (!invoiceTrimmed) {
      return toast.error("Invoice / Bill No is required");
    }
    const duplicateInvoice = (rows || []).some(
      (r) =>
        String(r.invoice_no || "").trim().toLowerCase() ===
          invoiceTrimmed.toLowerCase() &&
        (!editingRow || r.id !== editingRow.id),
    );
    if (duplicateInvoice) {
      return toast.error(
        `Invoice No "${invoiceTrimmed}" already exists. Please use a unique invoice number.`,
      );
    }

    const validLines = (form.lines || []).filter(
      (ln) => String(ln.item_id || "") && Number(ln.qty || 0) > 0,
    );
    if (validLines.length === 0) {
      return toast.error("Add at least one valid item line");
    }

    setSaving(true);
    try {
      if (editingRow?.id) {
        const ln = validLines[0];
        const qty = Number(ln.qty || 0);
        const rate = Number(ln.rate || 0);
        const amount = qty * rate;
        const item = itemMap.get(String(ln.item_id));
        await api.put(`/item-stock/${editingRow.id}`, {
          item_id: Number(ln.item_id),
          store_id: item?.store_id ? Number(item.store_id) : undefined,
          supplier_id: form.supplier_id ? Number(form.supplier_id) : undefined,
          purchase_date: form.purchase_date || undefined,
          invoice_no: String(form.invoice_no || "").trim() || undefined,
          qty: Number.isFinite(qty) ? qty : 0,
          rate: Number.isFinite(rate) ? rate : 0,
          amount: Number.isFinite(amount) ? amount : 0,
          remarks: String(form.remarks || "").trim() || undefined,
          remark: String(form.remarks || "").trim() || undefined,
          note: String(form.remarks || "").trim() || undefined,
          branch_id: branchId,
          session_id: sessionId,
        });
        toast.success("Purchase entry updated");
        setOpen(false);
        setEditingRow(null);
        load();
        return;
      }

      for (const ln of validLines) {
        const qty = Number(ln.qty || 0);
        const rate = Number(ln.rate || 0);
        const amount = qty * rate;
        const item = itemMap.get(String(ln.item_id));
        await api.post("/item-stock", {
          item_id: Number(ln.item_id),
          store_id: item?.store_id ? Number(item.store_id) : undefined,
          supplier_id: form.supplier_id ? Number(form.supplier_id) : undefined,
          purchase_date: form.purchase_date || undefined,
          invoice_no: String(form.invoice_no || "").trim() || undefined,
          qty: Number.isFinite(qty) ? qty : 0,
          rate: Number.isFinite(rate) ? rate : 0,
          amount: Number.isFinite(amount) ? amount : 0,
          remarks: String(form.remarks || "").trim() || undefined,
          remark: String(form.remarks || "").trim() || undefined,
          note: String(form.remarks || "").trim() || undefined,
          branch_id: branchId,
          session_id: sessionId,
        });
      }
      toast.success(`Saved ${validLines.length} stock line(s)`);
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const delPurchase = async () => {
    if (!deleteRow?.id) return;
    if (!permissions.canDelete)
      return toast.error("You do not have delete permission for stock");
    try {
      await api.delete(`/item-stock/${deleteRow.id}`);
      toast.success("Purchase entry deleted");
      setDeleteRow(null);
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div className="space-y-3" data-testid="inventory-stock-custom">
      <div className="border border-slate-200 bg-white rounded-md p-3 shadow-sm flex flex-wrap gap-2 items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-800">Stock Entries</h3>
        <div className="flex gap-2 w-full md:w-auto">
          <div className="relative flex-1 md:w-80">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search item / supplier / invoice / user"
              className="pl-9"
            />
          </div>
          <Button className="erp-primary" onClick={openCreate}>
            <Plus className="w-4 h-4 mr-1" />
            Add Purchase
          </Button>
        </div>
      </div>

      <div className="border border-slate-200 bg-white rounded-md overflow-hidden shadow-sm">
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr>
              <th className="text-left px-3 py-2">Purchase Date</th>
              <th className="text-left px-3 py-2">Invoice No</th>
              <th className="text-left px-3 py-2">Supplier</th>
              <th className="text-left px-3 py-2">Item</th>
              <th className="text-left px-3 py-2">Qty</th>
              <th className="text-left px-3 py-2">Rate</th>
              <th className="text-left px-3 py-2">Amount</th>
              <th className="text-left px-3 py-2">Remark</th>
              <th className="text-left px-3 py-2">Entered By</th>
              {(permissions.canEdit || permissions.canDelete) && (
                <th className="text-left px-3 py-2">Actions</th>
              )}
            </tr>
          </thead>
          <tbody>
            {filteredRows.map((row) => {
              const qty = Number(row.qty || 0);
              const rate = Number(row.rate || 0);
              const amount = Number(row.amount ?? qty * rate);
              return (
                <tr key={row.id} className="border-t">
                  <td className="px-3 py-2">{row.purchase_date || "-"}</td>
                  <td className="px-3 py-2">{row.invoice_no || "-"}</td>
                  <td className="px-3 py-2">
                    {supplierMap.get(String(row.supplier_id || "")) || "-"}
                  </td>
                  <td className="px-3 py-2">
                    {itemMap.get(String(row.item_id || ""))?.name || "-"}
                  </td>
                  <td className="px-3 py-2">{qty}</td>
                  <td className="px-3 py-2">{rate}</td>
                  <td className="px-3 py-2">
                    {Number.isFinite(amount) ? amount : 0}
                  </td>
                  <td className="px-3 py-2">
                    {row.remarks || row.remark || row.note || "-"}
                  </td>
                  <td className="px-3 py-2">{resolveEnteredBy(row)}</td>
                  {(permissions.canEdit || permissions.canDelete) && (
                    <td className="px-3 py-2">
                      <div className="flex gap-1">
                        {permissions.canEdit && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openEdit(row)}
                          >
                            <Pencil className="w-3.5 h-3.5 mr-1" />
                            Edit
                          </Button>
                        )}
                        {permissions.canDelete && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setDeleteRow(row)}
                          >
                            <Trash2 className="w-3.5 h-3.5 mr-1" />
                            Delete
                          </Button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              );
            })}
            {filteredRows.length === 0 && (
              <tr>
                <td
                  colSpan={
                    permissions.canEdit || permissions.canDelete ? 10 : 9
                  }
                  className="px-3 py-6 text-center text-slate-400"
                >
                  No stock entries found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-5xl">
          <DialogHeader>
            <DialogTitle>
              {editingRow ? "Edit Purchase Entry" : "Add Stock Purchase"}
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs font-semibold mb-1 block">
                Purchase Date
              </Label>
              <Input
                type="date"
                value={form.purchase_date}
                onChange={(e) =>
                  setForm((f) => ({ ...f, purchase_date: e.target.value }))
                }
              />
            </div>
            <SearchableSelect
              label={<>Supplier <span className="text-red-500">*</span></>}
              value={form.supplier_id}
              onChange={(v) => setForm((f) => ({ ...f, supplier_id: v }))}
              items={(suppliers || []).map((s) => ({
                id: s.id,
                name: s.name || `Supplier ${s.id}`,
                subtitle: s.contact || "",
              }))}
              placeholder="Search supplier"
            />
            <div>
              <Label className="text-xs font-semibold mb-1 block">
                Invoice No <span className="text-red-500">*</span>
              </Label>
              <Input
                value={form.invoice_no}
                onChange={(e) =>
                  setForm((f) => ({ ...f, invoice_no: e.target.value }))
                }
                placeholder="Enter invoice number"
              />
            </div>
          </div>

          <div className="border border-slate-200 rounded-md overflow-visible">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left px-3 py-2">Item</th>
                  <th className="text-left px-3 py-2">Qty</th>
                  <th className="text-left px-3 py-2">Rate</th>
                  <th className="text-left px-3 py-2">Amount</th>
                  <th className="text-left px-3 py-2 w-16">-</th>
                </tr>
              </thead>
              <tbody>
                {form.lines.map((line, idx) => {
                  const amount = Number(line.qty || 0) * Number(line.rate || 0);
                  return (
                    <tr key={`ln-${idx}`} className="border-t">
                      <td className="px-3 py-2">
                        <SearchableSelect
                          hideLabel
                          label="Item"
                          value={line.item_id}
                          onChange={(v) => setLine(idx, { item_id: v })}
                          items={(items || []).map((it) => ({
                            id: it.id,
                            name: it.name || `Item ${it.id}`,
                            subtitle: it.unit || "",
                            keywords: `${it.name || ""} ${it.code || ""} ${it.unit || ""}`,
                          }))}
                          placeholder="Search item"
                        />
                      </td>
                      <td className="px-3 py-2">
                        <Input
                          type="number"
                          min="0"
                          value={line.qty}
                          onChange={(e) =>
                            setLine(idx, { qty: e.target.value })
                          }
                        />
                      </td>
                      <td className="px-3 py-2">
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={line.rate}
                          onChange={(e) =>
                            setLine(idx, { rate: e.target.value })
                          }
                        />
                      </td>
                      <td className="px-3 py-2">
                        {Number.isFinite(amount) ? amount : 0}
                      </td>
                      <td className="px-3 py-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => removeLine(idx)}
                          disabled={form.lines.length <= 1}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div>
            {!editingRow && (
              <Button type="button" variant="outline" onClick={addLine}>
                <Plus className="w-4 h-4 mr-1" />
                Add Item Line
              </Button>
            )}
          </div>

          <div>
            <Label className="text-xs font-semibold mb-1 block">Remark</Label>
            <textarea
              className="w-full min-h-20 rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={form.remarks}
              onChange={(e) =>
                setForm((f) => ({ ...f, remarks: e.target.value }))
              }
              placeholder="Enter remark"
            />
          </div>

          <div className="mt-2 flex gap-2 justify-end">
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button className="erp-primary" onClick={save} disabled={saving}>
              {saving ? "Saving..." : editingRow ? "Update" : "Save"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={!!deleteRow}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) setDeleteRow(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete purchase entry?</AlertDialogTitle>
            <AlertDialogDescription>
              This purchase row will be permanently removed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeleteRow(null)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={delPurchase}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function InventoryIssuesModule() {
  const { branchId, sessionId, user } = useAuth();
  const permissions = useRouteModulePermissions("inventory", "issues");
  const [rows, setRows] = useState([]);
  const [items, setItems] = useState([]);
  const [stockRows, setStockRows] = useState([]);
  const [staffRows, setStaffRows] = useState([]);
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [deleteRow, setDeleteRow] = useState(null);
  const [form, setForm] = useState({
    staff_id: "",
    issued_to: "",
    date: new Date().toISOString().slice(0, 10),
    note: "",
    lines: [{ item_id: "", qty: "1" }],
  });

  const load = async () => {
    try {
      const [issueRes, itemsRes, stockRes, staffRes, usersRes] =
        await Promise.all([
          api.get("/item-issues", {
            params: { branch_id: branchId, limit: 5000 },
          }),
          api.get("/inventory-items", {
            params: { branch_id: branchId, limit: 5000 },
          }),
          api.get("/item-stock", {
            params: { branch_id: branchId, limit: 5000 },
          }),
          api.get("/staff", {
            params: { branch_id: branchId, session_id: sessionId, limit: 5000 },
          }),
          api.get("/auth/users", {
            params: { branch_id: branchId, session_id: sessionId, limit: 1000 },
          }),
        ]);
      setRows(issueRes.data.items || []);
      setItems(itemsRes.data.items || []);
      setStockRows(stockRes.data.items || []);
      setStaffRows(staffRes.data.items || []);
      setUsers(usersRes.data.items || []);
    } catch {
      toast.error("Failed to load issue entries");
      setRows([]);
    }
  };

  useEffect(() => {
    load();
  }, [branchId, sessionId]);

  const itemMap = useMemo(
    () => new Map((items || []).map((i) => [String(i.id), i.name])),
    [items],
  );
  const userMap = useMemo(
    () =>
      new Map(
        (users || []).map((u) => [
          String(u.id),
          u.name || u.full_name || u.email || `User ${u.id}`,
        ]),
      ),
    [users],
  );

  const resolveEnteredBy = useCallback(
    (row) => {
      const id = String(row.created_by || row.updated_by || "");
      if (!id) return "-";
      if (userMap.has(id)) return userMap.get(id);
      if (String(user?.id || "") === id)
        return user?.name || user?.email || "You";
      return `User ${id}`;
    },
    [userMap, user],
  );

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (rows || []).filter((r) => {
      if (!q) return true;
      const hay = [
        itemMap.get(String(r.item_id || "")),
        r.issued_to,
        r.date,
        r.note,
        r.remarks,
        resolveEnteredBy(r),
      ]
        .map((x) => String(x || "").toLowerCase())
        .join(" ");
      return hay.includes(q);
    });
  }, [rows, search, itemMap, resolveEnteredBy]);

  const currentStockMap = useMemo(() => {
    const purchased = new Map();
    const issued = new Map();

    (items || []).forEach((it) => {
      purchased.set(String(it.id || ""), Number(it.qty || 0) || 0);
    });

    (stockRows || []).forEach((s) => {
      const key = String(s.item_id || "");
      const qty = Number(s.qty || 0);
      purchased.set(
        key,
        (purchased.get(key) || 0) + (Number.isFinite(qty) ? qty : 0),
      );
    });

    (rows || []).forEach((s) => {
      const key = String(s.item_id || "");
      const qty = Number(s.qty || 0);
      issued.set(
        key,
        (issued.get(key) || 0) + (Number.isFinite(qty) ? qty : 0),
      );
    });

    const result = new Map();
    const keys = new Set([...purchased.keys(), ...issued.keys()]);
    keys.forEach((k) => {
      result.set(k, (purchased.get(k) || 0) - (issued.get(k) || 0));
    });
    return result;
  }, [items, stockRows, rows]);

  const staffOptions = useMemo(
    () =>
      (staffRows || []).map((s) => {
        const name =
          `${s.first_name || ""} ${s.last_name || ""}`.trim() ||
          s.name ||
          `Staff ${s.id}`;
        return {
          id: s.id,
          name,
          subtitle: s.role || s.designation || s.employee_id || "",
          keywords: `${name} ${s.employee_id || ""} ${s.role || ""} ${s.mobile || ""}`,
        };
      }),
    [staffRows],
  );

  const addLine = () => {
    setForm((f) => ({
      ...f,
      lines: [...f.lines, { item_id: "", qty: "1" }],
    }));
  };

  const removeLine = (idx) => {
    setForm((f) => ({
      ...f,
      lines: f.lines.filter((_, i) => i !== idx),
    }));
  };

  const setLine = (idx, patch) => {
    setForm((f) => ({
      ...f,
      lines: f.lines.map((ln, i) => (i === idx ? { ...ln, ...patch } : ln)),
    }));
  };

  const resetForm = () => {
    setForm({
      staff_id: "",
      issued_to: "",
      date: new Date().toISOString().slice(0, 10),
      note: "",
      lines: [{ item_id: "", qty: "1" }],
    });
  };

  const openCreate = () => {
    if (!permissions.canAdd)
      return toast.error("You do not have add permission for issue items");
    setEditingRow(null);
    resetForm();
    setOpen(true);
  };

  const openEdit = (row) => {
    if (!permissions.canEdit)
      return toast.error("You do not have edit permission for issue items");
    setEditingRow(row);
    setForm({
      staff_id: String(row.staff_id || ""),
      issued_to: String(row.issued_to || ""),
      date: String(row.date || new Date().toISOString().slice(0, 10)),
      note: String(row.note || row.remarks || ""),
      lines: [
        { item_id: String(row.item_id || ""), qty: String(row.qty ?? 0) },
      ],
    });
    setOpen(true);
  };

  const delIssue = async () => {
    if (!deleteRow?.id) return;
    if (!permissions.canDelete)
      return toast.error("You do not have delete permission for issue items");
    try {
      await api.delete(`/item-issues/${deleteRow.id}`);
      toast.success("Issue entry deleted");
      setDeleteRow(null);
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const save = async () => {
    if (editingRow && !permissions.canEdit)
      return toast.error("You do not have edit permission for issue items");
    if (!editingRow && !permissions.canAdd)
      return toast.error("You do not have add permission for issue items");

    const validLines = (form.lines || []).filter(
      (ln) => String(ln.item_id || "") && Number(ln.qty || 0) > 0,
    );
    if (validLines.length === 0)
      return toast.error("Add at least one valid item line");

    const requestedByItem = new Map();
    validLines.forEach((ln) => {
      const key = String(ln.item_id || "");
      const qty = Number(ln.qty || 0);
      requestedByItem.set(
        key,
        (requestedByItem.get(key) || 0) + (Number.isFinite(qty) ? qty : 0),
      );
    });

    for (const [itemId, requestedQty] of requestedByItem.entries()) {
      const current = Number(currentStockMap.get(itemId) || 0);
      const editingBoost =
        editingRow && String(editingRow.item_id || "") === String(itemId)
          ? Number(editingRow.qty || 0)
          : 0;
      const available = current + editingBoost;
      if (requestedQty > available) {
        const itemName = itemMap.get(String(itemId)) || `Item ${itemId}`;
        return toast.error(
          `${itemName}: issue qty (${requestedQty}) exceeds current stock (${available})`,
        );
      }
    }

    setSaving(true);

    try {
      if (editingRow?.id) {
        const line = validLines[0];
        await api.put(`/item-issues/${editingRow.id}`, {
          item_id: Number(line.item_id),
          issued_to: String(form.issued_to || "").trim() || undefined,
          qty: Number(line.qty || 0),
          date: form.date || undefined,
          note: String(form.note || "").trim() || undefined,
          remarks: String(form.note || "").trim() || undefined,
          staff_id: form.staff_id ? Number(form.staff_id) : undefined,
          branch_id: branchId,
          session_id: sessionId,
        });
        toast.success("Issue entry updated");
        setOpen(false);
        setEditingRow(null);
        load();
        return;
      }

      for (const ln of validLines) {
        await api.post("/item-issues", {
          item_id: Number(ln.item_id),
          issued_to: String(form.issued_to || "").trim() || undefined,
          qty: Number(ln.qty || 0),
          date: form.date || undefined,
          note: String(form.note || "").trim() || undefined,
          remarks: String(form.note || "").trim() || undefined,
          staff_id: form.staff_id ? Number(form.staff_id) : undefined,
          branch_id: branchId,
          session_id: sessionId,
        });
      }
      toast.success(`Saved ${validLines.length} issue line(s)`);
      setOpen(false);
      setEditingRow(null);
      resetForm();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="inventory-issues-custom">
      <div className="border border-slate-200 bg-white rounded-md p-3 shadow-sm flex flex-wrap gap-2 items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-800">Issue Items</h3>
        <div className="flex gap-2 w-full md:w-auto">
          <div className="relative flex-1 md:w-80">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search item / issued to / user"
              className="pl-9"
            />
          </div>
          <Button className="erp-primary" onClick={openCreate}>
            <Plus className="w-4 h-4 mr-1" />
            Add Issue
          </Button>
        </div>
      </div>

      <div className="border border-slate-200 bg-white rounded-md overflow-hidden shadow-sm">
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr>
              <th className="text-left px-3 py-2">Date</th>
              <th className="text-left px-3 py-2">Item</th>
              <th className="text-left px-3 py-2">Issued To</th>
              <th className="text-left px-3 py-2">Qty</th>
              <th className="text-left px-3 py-2">Remark</th>
              <th className="text-left px-3 py-2">Entered By</th>
              {(permissions.canEdit || permissions.canDelete) && (
                <th className="text-left px-3 py-2">Actions</th>
              )}
            </tr>
          </thead>
          <tbody>
            {filteredRows.map((row) => (
              <tr key={row.id} className="border-t">
                <td className="px-3 py-2">{row.date || "-"}</td>
                <td className="px-3 py-2">
                  {itemMap.get(String(row.item_id || "")) || "-"}
                </td>
                <td className="px-3 py-2">{row.issued_to || "-"}</td>
                <td className="px-3 py-2">{row.qty ?? 0}</td>
                <td className="px-3 py-2">{row.note || row.remarks || "-"}</td>
                <td className="px-3 py-2">{resolveEnteredBy(row)}</td>
                {(permissions.canEdit || permissions.canDelete) && (
                  <td className="px-3 py-2">
                    <div className="flex gap-1">
                      {permissions.canEdit && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openEdit(row)}
                        >
                          <Pencil className="w-3.5 h-3.5 mr-1" />
                          Edit
                        </Button>
                      )}
                      {permissions.canDelete && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setDeleteRow(row)}
                        >
                          <Trash2 className="w-3.5 h-3.5 mr-1" />
                          Delete
                        </Button>
                      )}
                    </div>
                  </td>
                )}
              </tr>
            ))}
            {filteredRows.length === 0 && (
              <tr>
                <td
                  colSpan={permissions.canEdit || permissions.canDelete ? 7 : 6}
                  className="px-3 py-6 text-center text-slate-400"
                >
                  No issue entries found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {editingRow ? "Edit Issue Item" : "Add Issue Item"}
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <SearchableSelect
              label="Staff (Searchable)"
              value={form.staff_id}
              onChange={(v) => {
                const selected = (staffRows || []).find(
                  (s) => String(s.id) === String(v),
                );
                const name =
                  `${selected?.first_name || ""} ${selected?.last_name || ""}`.trim() ||
                  selected?.name ||
                  "";
                setForm((f) => ({
                  ...f,
                  staff_id: v,
                  issued_to: name || f.issued_to,
                }));
              }}
              items={staffOptions}
              placeholder="Search staff"
            />
            <div>
              <Label className="text-xs font-semibold mb-1 block">Date</Label>
              <Input
                type="date"
                value={form.date}
                onChange={(e) =>
                  setForm((f) => ({ ...f, date: e.target.value }))
                }
              />
            </div>
            <div className="md:col-span-2 border border-slate-200 rounded-md overflow-visible">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left px-3 py-2">Item</th>
                    <th className="text-left px-3 py-2">Qty</th>
                    <th className="text-left px-3 py-2">Current Stock</th>
                    <th className="text-left px-3 py-2 w-16">-</th>
                  </tr>
                </thead>
                <tbody>
                  {form.lines.map((line, idx) => {
                    const key = String(line.item_id || "");
                    const current = Number(currentStockMap.get(key) || 0);
                    const available =
                      editingRow && String(editingRow.item_id || "") === key
                        ? current + Number(editingRow.qty || 0)
                        : current;

                    return (
                      <tr key={`issue-ln-${idx}`} className="border-t">
                        <td className="px-3 py-2">
                          <SearchableSelect
                            hideLabel
                            label="Item"
                            value={line.item_id}
                            onChange={(v) => setLine(idx, { item_id: v })}
                            items={(items || []).map((it) => ({
                              id: it.id,
                              name: it.name || `Item ${it.id}`,
                              subtitle: it.unit || "",
                              keywords: `${it.name || ""} ${it.code || ""} ${it.unit || ""}`,
                            }))}
                            placeholder="Search item"
                          />
                        </td>
                        <td className="px-3 py-2">
                          <Input
                            type="number"
                            min="0"
                            value={line.qty}
                            onChange={(e) =>
                              setLine(idx, { qty: e.target.value })
                            }
                          />
                        </td>
                        <td className="px-3 py-2">{available}</td>
                        <td className="px-3 py-2">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => removeLine(idx)}
                            disabled={form.lines.length <= 1}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div>
              <Label className="text-xs font-semibold mb-1 block">
                Issued To
              </Label>
              <Input
                value={form.issued_to}
                onChange={(e) =>
                  setForm((f) => ({ ...f, issued_to: e.target.value }))
                }
                placeholder="Person / Department"
              />
            </div>
            <div>
              {!editingRow && (
                <Button
                  type="button"
                  variant="outline"
                  className="mt-6"
                  onClick={addLine}
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add Item Line
                </Button>
              )}
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs font-semibold mb-1 block">Remark</Label>
              <textarea
                className="w-full min-h-20 rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={form.note}
                onChange={(e) =>
                  setForm((f) => ({ ...f, note: e.target.value }))
                }
                placeholder="Remark"
              />
            </div>
          </div>
          <div className="mt-2 flex gap-2 justify-end">
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button className="erp-primary" onClick={save} disabled={saving}>
              {saving ? "Saving..." : editingRow ? "Update" : "Save"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={!!deleteRow}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) setDeleteRow(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete issue entry?</AlertDialogTitle>
            <AlertDialogDescription>
              This issue row will be permanently removed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeleteRow(null)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={delIssue}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function escapeHtmlCell(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/\"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function downloadExcelTable(filename, headers, rows) {
  const head = `<tr>${headers
    .map((h) => `<th>${escapeHtmlCell(h)}</th>`)
    .join("")}</tr>`;
  const body = rows
    .map(
      (row) =>
        `<tr>${row.map((cell) => `<td>${escapeHtmlCell(cell)}</td>`).join("")}</tr>`,
    )
    .join("");
  const html = `\ufeff<html><head><meta charset=\"UTF-8\"></head><body><table border=\"1\">${head}${body}</table></body></html>`;
  const blob = new Blob([html], {
    type: "application/vnd.ms-excel;charset=utf-8;",
  });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
}

function InventoryPurchaseReportModule() {
  const { branchId, sessionId, user } = useAuth();
  const [rows, setRows] = useState([]);
  const [items, setItems] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [itemId, setItemId] = useState("");
  const [supplierId, setSupplierId] = useState("");

  const load = async () => {
    try {
      const [stockRes, itemsRes, supRes, usersRes] = await Promise.all([
        api.get("/item-stock", {
          params: { branch_id: branchId, limit: 5000 },
        }),
        api.get("/inventory-items", {
          params: { branch_id: branchId, limit: 5000 },
        }),
        api.get("/item-suppliers", {
          params: { branch_id: branchId, limit: 1000 },
        }),
        api.get("/auth/users", {
          params: { branch_id: branchId, session_id: sessionId, limit: 1000 },
        }),
      ]);
      setRows(stockRes.data.items || []);
      setItems(itemsRes.data.items || []);
      setSuppliers(supRes.data.items || []);
      setUsers(usersRes.data.items || []);
    } catch {
      toast.error("Failed to load purchase report");
      setRows([]);
    }
  };

  useEffect(() => {
    load();
  }, [branchId, sessionId]);

  const itemMap = useMemo(
    () =>
      new Map(
        (items || []).map((i) => [String(i.id), i.name || `Item ${i.id}`]),
      ),
    [items],
  );
  const supplierMap = useMemo(
    () =>
      new Map(
        (suppliers || []).map((s) => [
          String(s.id),
          s.name || `Supplier ${s.id}`,
        ]),
      ),
    [suppliers],
  );
  const userMap = useMemo(
    () =>
      new Map(
        (users || []).map((u) => [
          String(u.id),
          u.name || u.full_name || u.email || `User ${u.id}`,
        ]),
      ),
    [users],
  );

  const resolveEnteredBy = useCallback(
    (row) => {
      const id = String(row.created_by || row.updated_by || "");
      if (!id) return "-";
      if (userMap.has(id)) return userMap.get(id);
      if (String(user?.id || "") === id)
        return user?.name || user?.email || "You";
      return `User ${id}`;
    },
    [userMap, user],
  );

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (rows || []).filter((r) => {
      const date = String(r.purchase_date || "");
      if (fromDate && date && date < fromDate) return false;
      if (toDate && date && date > toDate) return false;
      if (itemId && String(r.item_id || "") !== String(itemId)) return false;
      if (supplierId && String(r.supplier_id || "") !== String(supplierId))
        return false;

      if (!q) return true;
      const hay = [
        itemMap.get(String(r.item_id || "")),
        supplierMap.get(String(r.supplier_id || "")),
        r.invoice_no,
        r.purchase_date,
        r.remarks,
        r.remark,
        resolveEnteredBy(r),
      ]
        .map((x) => String(x || "").toLowerCase())
        .join(" ");
      return hay.includes(q);
    });
  }, [
    rows,
    search,
    fromDate,
    toDate,
    itemId,
    supplierId,
    itemMap,
    supplierMap,
    resolveEnteredBy,
  ]);

  const downloadExcel = () => {
    const tableRows = filteredRows.map((r) => {
      const qty = Number(r.qty || 0);
      const rate = Number(r.rate || 0);
      const amount = Number(r.amount ?? qty * rate);
      return [
        r.purchase_date || "",
        r.invoice_no || "",
        supplierMap.get(String(r.supplier_id || "")) || "",
        itemMap.get(String(r.item_id || "")) || "",
        qty,
        rate,
        Number.isFinite(amount) ? amount : 0,
        r.remarks || r.remark || r.note || "",
        resolveEnteredBy(r),
      ];
    });

    downloadExcelTable(
      "purchase_report.xls",
      [
        "Purchase Date",
        "Invoice No",
        "Supplier",
        "Item",
        "Qty",
        "Rate",
        "Amount",
        "Remark",
        "Entered By",
      ],
      tableRows,
    );
  };

  return (
    <div className="space-y-3" data-testid="inventory-purchase-report">
      <div className="border border-slate-200 bg-white rounded-md p-3 shadow-sm">
        <div className="mb-3 flex items-center justify-between gap-2">
          <h3 className="text-sm font-semibold text-slate-800">
            Purchase Report
          </h3>
          <Button variant="outline" onClick={downloadExcel}>
            Download Excel
          </Button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search"
          />
          <Input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
          />
          <Input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
          />
          <select
            className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
            value={itemId}
            onChange={(e) => setItemId(e.target.value)}
          >
            <option value="">All Items</option>
            {(items || []).map((it) => (
              <option key={it.id} value={String(it.id)}>
                {it.name || `Item ${it.id}`}
              </option>
            ))}
          </select>
          <select
            className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
            value={supplierId}
            onChange={(e) => setSupplierId(e.target.value)}
          >
            <option value="">All Suppliers</option>
            {(suppliers || []).map((sp) => (
              <option key={sp.id} value={String(sp.id)}>
                {sp.name || `Supplier ${sp.id}`}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="border border-slate-200 bg-white rounded-md overflow-hidden shadow-sm">
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr>
              <th className="text-left px-3 py-2">Purchase Date</th>
              <th className="text-left px-3 py-2">Invoice No</th>
              <th className="text-left px-3 py-2">Supplier</th>
              <th className="text-left px-3 py-2">Item</th>
              <th className="text-left px-3 py-2">Qty</th>
              <th className="text-left px-3 py-2">Rate</th>
              <th className="text-left px-3 py-2">Amount</th>
              <th className="text-left px-3 py-2">Remark</th>
              <th className="text-left px-3 py-2">Entered By</th>
            </tr>
          </thead>
          <tbody>
            {filteredRows.map((row) => {
              const qty = Number(row.qty || 0);
              const rate = Number(row.rate || 0);
              const amount = Number(row.amount ?? qty * rate);
              return (
                <tr key={row.id} className="border-t">
                  <td className="px-3 py-2">{row.purchase_date || "-"}</td>
                  <td className="px-3 py-2">{row.invoice_no || "-"}</td>
                  <td className="px-3 py-2">
                    {supplierMap.get(String(row.supplier_id || "")) || "-"}
                  </td>
                  <td className="px-3 py-2">
                    {itemMap.get(String(row.item_id || "")) || "-"}
                  </td>
                  <td className="px-3 py-2">{qty}</td>
                  <td className="px-3 py-2">{rate}</td>
                  <td className="px-3 py-2">
                    {Number.isFinite(amount) ? amount : 0}
                  </td>
                  <td className="px-3 py-2">
                    {row.remarks || row.remark || row.note || "-"}
                  </td>
                  <td className="px-3 py-2">{resolveEnteredBy(row)}</td>
                </tr>
              );
            })}
            {filteredRows.length === 0 && (
              <tr>
                <td
                  colSpan={9}
                  className="px-3 py-6 text-center text-slate-400"
                >
                  No purchase report rows found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function InventoryStockReportModule() {
  const { branchId } = useAuth();
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [stockRows, setStockRows] = useState([]);
  const [issueRows, setIssueRows] = useState([]);
  const [search, setSearch] = useState("");
  const [itemId, setItemId] = useState("");
  const [categoryId, setCategoryId] = useState("");

  const load = async () => {
    try {
      const [itemsRes, categoryRes, stockRes, issueRes] = await Promise.all([
        api.get("/inventory-items", {
          params: { branch_id: branchId, limit: 5000 },
        }),
        api.get("/item-categories", {
          params: { branch_id: branchId, limit: 1000 },
        }),
        api.get("/item-stock", {
          params: { branch_id: branchId, limit: 5000 },
        }),
        api.get("/item-issues", {
          params: { branch_id: branchId, limit: 5000 },
        }),
      ]);
      setItems(itemsRes.data.items || []);
      setCategories(categoryRes.data.items || []);
      setStockRows(stockRes.data.items || []);
      setIssueRows(issueRes.data.items || []);
    } catch {
      toast.error("Failed to load stock report");
      setItems([]);
    }
  };

  useEffect(() => {
    load();
  }, [branchId]);

  const categoryMap = useMemo(
    () =>
      new Map(
        (categories || []).map((c) => [
          String(c.id),
          c.name || `Category ${c.id}`,
        ]),
      ),
    [categories],
  );

  const reportRows = useMemo(() => {
    const purchasedMap = new Map();
    const issuedMap = new Map();

    (stockRows || []).forEach((r) => {
      const key = String(r.item_id || "");
      const q = Number(r.qty || 0);
      purchasedMap.set(
        key,
        (purchasedMap.get(key) || 0) + (Number.isFinite(q) ? q : 0),
      );
    });

    (issueRows || []).forEach((r) => {
      const key = String(r.item_id || "");
      const q = Number(r.qty || 0);
      issuedMap.set(
        key,
        (issuedMap.get(key) || 0) + (Number.isFinite(q) ? q : 0),
      );
    });

    const q = search.trim().toLowerCase();
    return (items || [])
      .map((it) => {
        const key = String(it.id || "");
        const openingQty = Number(it.qty || 0);
        const purchasedQty = purchasedMap.get(key) || 0;
        const issuedQty = issuedMap.get(key) || 0;
        return {
          id: it.id,
          item_name: it.name || `Item ${it.id}`,
          category_id: it.category_id || "",
          category_name: categoryMap.get(String(it.category_id || "")) || "-",
          unit: it.unit || "PCS",
          opening_qty: openingQty,
          purchased_qty: purchasedQty,
          issued_qty: issuedQty,
          current_stock: openingQty + purchasedQty - issuedQty,
          min_qty: Number(it.min_qty || 0),
        };
      })
      .filter((r) => {
        if (itemId && String(r.id) !== String(itemId)) return false;
        if (categoryId && String(r.category_id || "") !== String(categoryId))
          return false;
        if (!q) return true;
        const hay = [r.item_name, r.category_name, r.unit]
          .join(" ")
          .toLowerCase();
        return hay.includes(q);
      });
  }, [items, stockRows, issueRows, search, itemId, categoryId, categoryMap]);

  const downloadExcel = () => {
    const tableRows = reportRows.map((r) => [
      r.item_name,
      r.category_name,
      r.unit,
      r.opening_qty,
      r.purchased_qty,
      r.issued_qty,
      r.current_stock,
      r.min_qty,
    ]);

    downloadExcelTable(
      "stock_report.xls",
      [
        "Item",
        "Category",
        "Unit",
        "Opening Qty",
        "Purchased Qty",
        "Issued Qty",
        "Current Stock",
        "Min Qty",
      ],
      tableRows,
    );
  };

  return (
    <div className="space-y-3" data-testid="inventory-stock-report">
      <div className="border border-slate-200 bg-white rounded-md p-3 shadow-sm">
        <div className="mb-3 flex items-center justify-between gap-2">
          <h3 className="text-sm font-semibold text-slate-800">Stock Report</h3>
          <Button variant="outline" onClick={downloadExcel}>
            Download Excel
          </Button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search item/category"
          />
          <select
            className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
            value={categoryId}
            onChange={(e) => setCategoryId(e.target.value)}
          >
            <option value="">All Categories</option>
            {(categories || []).map((c) => (
              <option key={c.id} value={String(c.id)}>
                {c.name || `Category ${c.id}`}
              </option>
            ))}
          </select>
          <select
            className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
            value={itemId}
            onChange={(e) => setItemId(e.target.value)}
          >
            <option value="">All Items</option>
            {(items || []).map((it) => (
              <option key={it.id} value={String(it.id)}>
                {it.name || `Item ${it.id}`}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="border border-slate-200 bg-white rounded-md overflow-hidden shadow-sm">
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr>
              <th className="text-left px-3 py-2">Item</th>
              <th className="text-left px-3 py-2">Category</th>
              <th className="text-left px-3 py-2">Unit</th>
              <th className="text-left px-3 py-2">Opening Qty</th>
              <th className="text-left px-3 py-2">Purchased Qty</th>
              <th className="text-left px-3 py-2">Issued Qty</th>
              <th className="text-left px-3 py-2">Current Stock</th>
              <th className="text-left px-3 py-2">Min Qty</th>
            </tr>
          </thead>
          <tbody>
            {reportRows.map((row) => (
              <tr key={row.id} className="border-t">
                <td className="px-3 py-2">{row.item_name}</td>
                <td className="px-3 py-2">{row.category_name}</td>
                <td className="px-3 py-2">{row.unit}</td>
                <td className="px-3 py-2">{row.opening_qty}</td>
                <td className="px-3 py-2">{row.purchased_qty}</td>
                <td className="px-3 py-2">{row.issued_qty}</td>
                <td className="px-3 py-2">{row.current_stock}</td>
                <td className="px-3 py-2">{row.min_qty}</td>
              </tr>
            ))}
            {reportRows.length === 0 && (
              <tr>
                <td
                  colSpan={8}
                  className="px-3 py-6 text-center text-slate-400"
                >
                  No stock report rows found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Sub({ items, prefix }) {
  return (
    <div className="border-b border-slate-200 mb-3 flex flex-wrap gap-1 text-xs">
      {items.map((i) => (
        <NavLink
          key={i.to}
          to={i.to}
          end={i.end}
          data-testid={`${prefix}-${i.to.split("/").pop()}`}
          className={({ isActive }) =>
            `px-3 py-1.5 border-b-2 -mb-px ${isActive ? "border-blue-600 text-blue-600 font-medium" : "border-transparent text-slate-600 hover:text-slate-900"}`
          }
        >
          {i.label}
        </NavLink>
      ))}
    </div>
  );
}

export function LibraryPage() {
  return (
    <div data-testid="library-page">
      <Sub
        prefix="lib"
        items={[
          { to: "/library/books", label: "Books" },
          { to: "/library/issues", label: "Issue / Return" },
          { to: "/library/members", label: "Members" },
          { to: "/library/card", label: "Library Card" },
          { to: "/library/fines", label: "Fines" },
          { to: "/library/authors", label: "Authors" },
          { to: "/library/publishers", label: "Publishers" },
          { to: "/library/racks", label: "Racks" },
          { to: "/library/categories", label: "Categories" },
        ]}
      />
      <Routes>
        <Route index element={<Navigate to="/library/books" replace />} />
        <Route path="books" element={<LibraryBooksModule />} />
        <Route path="issues" element={<LibraryIssueReturnModule />} />
        <Route path="members" element={<LibraryMembersModule />} />
        <Route path="card" element={<LibraryCardBlueprint />} />
        <Route path="fines" element={<LibraryFinesModule />} />
        <Route
          path="authors"
          element={
            <MasterCrudPage
              title="Authors"
              endpoint="/authors"
              testid="authors"
              fields={[{ key: "name", label: "Name *", required: true }]}
            />
          }
        />
        <Route
          path="publishers"
          element={
            <MasterCrudPage
              title="Publishers"
              endpoint="/publishers"
              testid="publishers"
              fields={[{ key: "name", label: "Name *", required: true }]}
            />
          }
        />
        <Route
          path="racks"
          element={
            <MasterCrudPage
              title="Racks"
              endpoint="/racks"
              testid="racks"
              fields={[
                { key: "name", label: "Rack/Shelf *", required: true },
                { key: "location", label: "Location" },
              ]}
            />
          }
        />
        <Route
          path="categories"
          element={
            <MasterCrudPage
              title="Book Categories"
              endpoint="/book-categories"
              testid="bcat"
              fields={[{ key: "name", label: "Name *", required: true }]}
            />
          }
        />
      </Routes>
    </div>
  );
}

export function InventoryPage() {
  return (
    <div data-testid="inventory-page">
      <Sub
        prefix="inv"
        items={[
          { to: "/inventory/items", label: "Items" },
          { to: "/inventory/stock", label: "Stock" },
          { to: "/inventory/issues", label: "Issue Item" },
          { to: "/inventory/purchase-report", label: "Purchase Report" },
          { to: "/inventory/stock-report", label: "Stock Report" },
          { to: "/inventory/categories", label: "Categories" },
          { to: "/inventory/suppliers", label: "Suppliers" },
          { to: "/inventory/stores", label: "Stores" },
        ]}
      />
      <Routes>
        <Route index element={<Navigate to="/inventory/items" replace />} />
        <Route path="items" element={<InventoryItemsModule />} />
        <Route path="stock" element={<InventoryStockModule />} />
        <Route path="issues" element={<InventoryIssuesModule />} />
        <Route
          path="purchase-report"
          element={<InventoryPurchaseReportModule />}
        />
        <Route path="stock-report" element={<InventoryStockReportModule />} />
        <Route
          path="categories"
          element={
            <MasterCrudPage
              title="Item Categories"
              endpoint="/item-categories"
              testid="icat"
              fields={[{ key: "name", label: "Name *", required: true }]}
            />
          }
        />
        <Route
          path="suppliers"
          element={
            <MasterCrudPage
              title="Suppliers"
              endpoint="/item-suppliers"
              testid="suppliers"
              fields={[
                { key: "name", label: "Name *", required: true },
                { key: "contact", label: "Contact" },
                {
                  key: "address",
                  label: "Address",
                  type: "textarea",
                  full: true,
                },
                { key: "gst", label: "GST" },
              ]}
            />
          }
        />
        <Route
          path="stores"
          element={
            <MasterCrudPage
              title="Stores"
              endpoint="/item-stores"
              testid="stores"
              fields={[
                { key: "name", label: "Store Name *", required: true },
                { key: "location", label: "Location" },
              ]}
            />
          }
        />
      </Routes>
    </div>
  );
}

export function TransportPage() {
  return (
    <div data-testid="transport-page">
      <Sub
        prefix="tp"
        items={[
          { to: "/transport/vehicles", label: "Vehicles" },
          { to: "/transport/routes", label: "Routes" },
          { to: "/transport/areas", label: "Areas" },
          { to: "/transport/logs", label: "Bus Logs" },
          { to: "/transport/refueling", label: "Refueling" },
          { to: "/transport/maintenance", label: "Maintenance" },
          { to: "/transport/complaints", label: "Complaints" },
        ]}
      />
      <Routes>
        <Route
          index
          element={
            <MasterCrudPage
              title="Vehicles"
              endpoint="/vehicles"
              testid="vehicles"
              fields={[
                { key: "name", label: "Bus Name/No *", required: true },
                { key: "vehicle_no", label: "Vehicle No" },
                { key: "capacity", label: "Capacity", type: "number" },
                { key: "driver_name", label: "Driver" },
                { key: "driver_phone", label: "Driver Phone" },
                { key: "conductor", label: "Conductor" },
              ]}
            />
          }
        />
        <Route
          path="vehicles"
          element={
            <MasterCrudPage
              title="Vehicles"
              endpoint="/vehicles"
              testid="vehicles"
              fields={[
                { key: "name", label: "Bus Name/No *", required: true },
                { key: "vehicle_no", label: "Vehicle No" },
                { key: "capacity", label: "Capacity", type: "number" },
                { key: "driver_name", label: "Driver" },
                { key: "driver_phone", label: "Driver Phone" },
                { key: "conductor", label: "Conductor" },
              ]}
            />
          }
        />
        <Route
          path="routes"
          element={
            <MasterCrudPage
              title="Routes"
              endpoint="/routes"
              testid="routes"
              fields={[
                { key: "name", label: "Route *", required: true },
                { key: "vehicle_id", label: "Vehicle ID" },
                {
                  key: "stops",
                  label: "Stops (comma)",
                  type: "textarea",
                  full: true,
                },
                { key: "fee", label: "Fee", type: "number" },
              ]}
            />
          }
        />
        <Route
          path="areas"
          element={
            <MasterCrudPage
              title="Areas"
              endpoint="/areas"
              testid="areas"
              fields={[
                { key: "name", label: "Area Name *", required: true },
                { key: "pin", label: "Pin Code" },
              ]}
            />
          }
        />
        <Route
          path="logs"
          element={
            <MasterCrudPage
              title="Bus Logs"
              endpoint="/bus-logs"
              testid="bus-logs"
              fields={[
                { key: "vehicle_id", label: "Vehicle ID" },
                { key: "date", label: "Date", type: "date" },
                {
                  key: "trip",
                  label: "Trip",
                  type: "select",
                  options: [
                    { value: "morning", label: "Morning" },
                    { value: "evening", label: "Evening" },
                  ],
                },
                { key: "start_km", label: "Start KM", type: "number" },
                { key: "end_km", label: "End KM", type: "number" },
                { key: "notes", label: "Notes", type: "textarea", full: true },
              ]}
            />
          }
        />
        <Route
          path="refueling"
          element={
            <MasterCrudPage
              title="Refueling"
              endpoint="/bus-refueling"
              testid="refueling"
              fields={[
                { key: "vehicle_id", label: "Vehicle ID" },
                { key: "date", label: "Date", type: "date" },
                { key: "liters", label: "Liters", type: "number" },
                { key: "amount", label: "Amount", type: "number" },
                { key: "station", label: "Station" },
              ]}
            />
          }
        />
        <Route
          path="maintenance"
          element={
            <MasterCrudPage
              title="Maintenance"
              endpoint="/bus-maintenance"
              testid="maintenance"
              fields={[
                { key: "vehicle_id", label: "Vehicle ID" },
                { key: "date", label: "Date", type: "date" },
                { key: "type", label: "Type" },
                { key: "amount", label: "Amount", type: "number" },
                { key: "notes", label: "Notes", type: "textarea", full: true },
              ]}
            />
          }
        />
        <Route
          path="complaints"
          element={
            <MasterCrudPage
              title="Bus Complaints"
              endpoint="/bus-complaints"
              testid="bus-complaints"
              fields={[
                { key: "vehicle_id", label: "Vehicle ID" },
                { key: "complainant", label: "Complainant" },
                { key: "date", label: "Date", type: "date" },
                {
                  key: "status",
                  label: "Status",
                  type: "select",
                  options: [
                    { value: "open", label: "Open" },
                    { value: "resolved", label: "Resolved" },
                  ],
                },
                {
                  key: "description",
                  label: "Description",
                  type: "textarea",
                  full: true,
                },
              ]}
            />
          }
        />
      </Routes>
    </div>
  );
}

const CONTENT_TYPES = ["Worksheet", "Study Material", "Syllabus", "Notice", "Other"];
const AUDIENCE_OPTIONS = [
  { value: "all", label: "All" },
  { value: "student", label: "Student" },
  { value: "staff", label: "Staff/Teacher" },
];

function fileUrlFor(path) {
  if (!path) return "";
  return /^https?:\/\//i.test(path) ? path : `${BACKEND_URL}${path}`;
}

/** Shared upload-form fields used by Download Center / Circulars / Student Circulars. */
function ClassSectionTargeting({ form, setForm, classes, sections }) {
  const classIds = form.class_ids || [];
  const sectionOptions =
    classIds.length === 1
      ? sections
          .filter((s) => String(s.class_id) === String(classIds[0]))
          .map((s) => ({ label: s.name, value: s.id }))
      : [];

  return (
    <div className="space-y-3">
      <label className="flex items-center gap-2 text-xs font-semibold text-slate-700">
        <input
          type="checkbox"
          checked={!!form.all_classes}
          onChange={(e) =>
            setForm((f) => ({ ...f, all_classes: e.target.checked }))
          }
          className="w-3.5 h-3.5"
          data-testid="all-classes-checkbox"
        />
        Available For All Classes
      </label>
      {!form.all_classes && (
        <>
          <div>
            <Label className="text-xs font-semibold mb-1 block">Class</Label>
            <MultiCheck
              testid="target-classes"
              value={classIds}
              onChange={(v) =>
                setForm((f) => ({ ...f, class_ids: v, section_ids: [] }))
              }
              options={classes.map((c) => ({ label: c.name, value: c.id }))}
            />
          </div>
          {sectionOptions.length > 0 && (
            <div>
              <Label className="text-xs font-semibold mb-1 block">
                Section (optional — leave blank for whole class)
              </Label>
              <MultiCheck
                testid="target-sections"
                value={form.section_ids || []}
                onChange={(v) => setForm((f) => ({ ...f, section_ids: v }))}
                options={sectionOptions}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}

function DownloadCenterPage() {
  const permissions = useRouteModulePermissions("communicate", "downloads");
  const { branchId } = useAuth();
  const [rows, setRows] = useState([]);
  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);
  const [uploaders, setUploaders] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteRow, setDeleteRow] = useState(null);

  const [filterClass, setFilterClass] = useState("");
  const [filterType, setFilterType] = useState("");
  const [filterUploadedBy, setFilterUploadedBy] = useState("");
  const [filterFileType, setFilterFileType] = useState("");
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    const { data } = await api.get("/download-center", {
      params: {
        branch_id: branchId,
        class_id: filterClass || undefined,
        category: filterType || undefined,
        created_by: filterUploadedBy || undefined,
        file_type: filterFileType || undefined,
        q: q || undefined,
      },
    });
    setRows(data.items || []);
  }, [branchId, filterClass, filterType, filterUploadedBy, filterFileType, q]);

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load]);

  useEffect(() => {
    (async () => {
      const [c, s] = await Promise.all([
        api.get("/classes", { params: { branch_id: branchId, limit: 200 } }),
        api.get("/sections", { params: { branch_id: branchId, limit: 500 } }),
      ]);
      setClasses(c.data.items || []);
      setSections(s.data.items || []);
    })();
  }, [branchId]);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/download-center", {
        params: { branch_id: branchId, limit: 500 },
      });
      const seen = new Map();
      (data.items || []).forEach((r) => {
        if (r.created_by && !seen.has(r.created_by)) {
          seen.set(r.created_by, r.uploaded_by_name || `User #${r.created_by}`);
        }
      });
      setUploaders(Array.from(seen, ([id, name]) => ({ id, name })));
    })();
  }, [branchId]);

  const openCreate = () => {
    setEditing(null);
    setForm({ all_classes: false, audience: "all", publish_date: "" });
    setFile(null);
    setOpen(true);
  };
  const openEdit = (row) => {
    setEditing(row);
    setForm({
      title: row.title,
      description: row.description,
      category: row.category,
      audience: row.audience || "all",
      publish_date: row.publish_date,
      all_classes: !!row.all_classes,
      class_ids: (row.targets || []).map((t) => t.class_id),
      section_ids: (row.targets || [])
        .map((t) => t.section_id)
        .filter(Boolean),
    });
    setFile(null);
    setOpen(true);
  };

  const save = async () => {
    const title = (form.title || "").trim();
    if (!title) {
      toast.error("Content title is required");
      return;
    }
    setSaving(true);
    try {
      let fileUrl = editing?.file_url;
      let fileType = editing?.file_type;
      if (file) {
        const fd = new FormData();
        fd.append("file", file, file.name);
        const { data } = await api.post(
          "/upload?tag=download-center",
          fd,
          { headers: { "Content-Type": "multipart/form-data" } },
        );
        fileUrl = data.url;
        fileType = (file.name.split(".").pop() || "").toLowerCase();
      }

      const body = {
        title,
        description: form.description || "",
        category: form.category || "",
        audience: form.audience || "all",
        publish_date: form.publish_date || "",
        all_classes: !!form.all_classes,
        class_ids: form.all_classes ? [] : form.class_ids || [],
        section_ids: form.all_classes ? [] : form.section_ids || [],
        branch_id: branchId || "",
        file_url: fileUrl || "",
        file_type: fileType || "",
      };

      if (editing) {
        await api.put(`/download-center/${editing.id}`, body);
      } else {
        await api.post("/download-center", body);
      }
      toast.success(editing ? "Updated" : "Content uploaded");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const del = async () => {
    if (!deleteRow?.id) return;
    await api.delete(`/download-center/${deleteRow.id}`);
    toast.success("Deleted");
    setDeleteRow(null);
    load();
  };

  return (
    <div data-testid="download-center-page">
      <PageTitle
        title="Download Center"
        subtitle="Upload worksheets, study material and other content for classes"
        actions={
          permissions.canAdd ? (
            <Button
              size="sm"
              onClick={openCreate}
              className="h-8 erp-primary"
              data-testid="dc-add"
            >
              <Plus className="w-3.5 h-3.5 mr-1" /> Upload Content
            </Button>
          ) : null
        }
      />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Content" : "Upload Content"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs font-semibold mb-1 block">
                Content Title *
              </Label>
              <Input
                value={form.title || ""}
                onChange={(e) =>
                  setForm((f) => ({ ...f, title: e.target.value }))
                }
                className="h-9 text-sm"
                data-testid="dc-title"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold mb-1 block">
                Content Type
              </Label>
              <select
                className="h-9 w-full rounded-md border border-slate-300 text-sm px-2 bg-white"
                value={form.category || ""}
                onChange={(e) =>
                  setForm((f) => ({ ...f, category: e.target.value }))
                }
                data-testid="dc-category"
              >
                <option value="">Select</option>
                {CONTENT_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-xs font-semibold mb-1 block">
                Available For
              </Label>
              <select
                className="h-9 w-full rounded-md border border-slate-300 text-sm px-2 bg-white"
                value={form.audience || "all"}
                onChange={(e) =>
                  setForm((f) => ({ ...f, audience: e.target.value }))
                }
                data-testid="dc-audience"
              >
                {AUDIENCE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
            <ClassSectionTargeting
              form={form}
              setForm={setForm}
              classes={classes}
              sections={sections}
            />
            <div>
              <Label className="text-xs font-semibold mb-1 block">
                Upload Date
              </Label>
              <DatePicker
                value={form.publish_date || ""}
                onChange={(v) =>
                  setForm((f) => ({ ...f, publish_date: v }))
                }
                className="h-9 text-sm w-full"
                testId="dc-publish-date"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold mb-1 block">
                Description
              </Label>
              <textarea
                className="w-full rounded-md border border-slate-300 text-sm px-2 py-1.5 min-h-[70px]"
                value={form.description || ""}
                onChange={(e) =>
                  setForm((f) => ({ ...f, description: e.target.value }))
                }
                data-testid="dc-description"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold mb-1 block">
                File{" "}
                {editing?.file_url && (
                  <span className="font-normal text-slate-400">
                    (leave blank to keep existing file)
                  </span>
                )}
              </Label>
              <input
                type="file"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="text-xs"
                data-testid="dc-file"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              className="erp-primary"
              onClick={save}
              disabled={saving}
              data-testid="dc-save"
            >
              {editing ? "Update" : "Upload"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div>
          <div className="flex flex-wrap gap-2 mb-3">
            <select
              className="h-8 rounded-md border border-slate-300 text-xs px-2 bg-white"
              value={filterClass}
              onChange={(e) => setFilterClass(e.target.value)}
              data-testid="filter-dc-class"
            >
              <option value="">All Classes</option>
              {classes.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
            <select
              className="h-8 rounded-md border border-slate-300 text-xs px-2 bg-white"
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              data-testid="filter-dc-type"
            >
              <option value="">All Types</option>
              {CONTENT_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
            <select
              className="h-8 rounded-md border border-slate-300 text-xs px-2 bg-white"
              value={filterUploadedBy}
              onChange={(e) => setFilterUploadedBy(e.target.value)}
              data-testid="filter-dc-uploader"
            >
              <option value="">Uploaded By: All</option>
              {uploaders.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name}
                </option>
              ))}
            </select>
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                placeholder="Search title / description…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="h-8 pl-7 w-56 text-xs"
                data-testid="search-dc"
              />
            </div>
          </div>

          <DataTable
            testid="download-center-table"
            columns={[
              { key: "uploaded_by_name", label: "Uploaded By" },
              { key: "title", label: "Content Title" },
              {
                key: "description",
                label: "Description",
                render: (r) => (
                  <span className="line-clamp-2 max-w-xs block text-xs">
                    {r.description}
                  </span>
                ),
              },
              { key: "category", label: "Type" },
              { key: "publish_date", label: "Date" },
              { key: "audience", label: "Available For" },
              { key: "class_summary", label: "Class" },
              { key: "file_type", label: "File Type" },
              {
                key: "actions",
                label: "Action",
                render: (r) => (
                  <div className="flex items-center gap-1 justify-end">
                    {r.file_url && (
                      <a
                        href={fileUrlFor(r.file_url)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-1.5 rounded hover:bg-blue-50 erp-text-primary"
                        title="View"
                        data-testid={`dc-view-${r.id}`}
                      >
                        <Eye className="w-3.5 h-3.5" />
                      </a>
                    )}
                    {r.file_url && (
                      <a
                        href={fileUrlFor(r.file_url)}
                        download
                        className="p-1.5 rounded hover:bg-emerald-50 text-emerald-700"
                        title="Download"
                        data-testid={`dc-download-${r.id}`}
                      >
                        <Download className="w-3.5 h-3.5" />
                      </a>
                    )}
                    {permissions.canEdit && (
                      <button
                        className="p-1.5 rounded hover:bg-blue-50 erp-text-primary"
                        onClick={() => openEdit(r)}
                        title="Edit"
                        data-testid={`dc-edit-${r.id}`}
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                    )}
                    {permissions.canDelete && (
                      <button
                        className="p-1.5 rounded hover:bg-red-50 text-red-600"
                        onClick={() => setDeleteRow(r)}
                        title="Delete"
                        data-testid={`dc-delete-${r.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                ),
              },
            ]}
            rows={rows}
          />
      </div>

      <AlertDialog
        open={!!deleteRow}
        onOpenChange={(v) => !v && setDeleteRow(null)}
      >
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete "{deleteRow?.title}"?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove this content and its file.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={del}
              className="bg-rose-600 hover:bg-rose-700 text-white"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

const CIRCULAR_AUDIENCE_OPTIONS = [
  { value: "staff", label: "Staff/Teacher" },
  { value: "all", label: "All" },
];

/** Shared implementation for Circulars (staff-facing) and Student Circulars (student-facing). */
function CircularsLikePage({ title, endpoint, testid, permissionKey, hasAudience }) {
  const permissions = useRouteModulePermissions("communicate", permissionKey);
  const { branchId } = useAuth();
  const [rows, setRows] = useState([]);
  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteRow, setDeleteRow] = useState(null);
  const [filterClass, setFilterClass] = useState("");
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    const { data } = await api.get(endpoint, {
      params: {
        branch_id: branchId,
        class_id: filterClass || undefined,
        q: q || undefined,
      },
    });
    setRows(data.items || []);
  }, [endpoint, branchId, filterClass, q]);

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load]);

  useEffect(() => {
    (async () => {
      const [c, s] = await Promise.all([
        api.get("/classes", { params: { branch_id: branchId, limit: 200 } }),
        api.get("/sections", { params: { branch_id: branchId, limit: 500 } }),
      ]);
      setClasses(c.data.items || []);
      setSections(s.data.items || []);
    })();
  }, [branchId]);

  const openCreate = () => {
    setEditing(null);
    setForm({
      all_classes: false,
      audience: hasAudience ? "staff" : undefined,
      date: "",
    });
    setFile(null);
    setOpen(true);
  };
  const openEdit = (row) => {
    setEditing(row);
    setForm({
      title: row.title,
      content: row.content,
      audience: row.audience || "staff",
      date: row.date,
      all_classes: !!row.all_classes,
      class_ids: (row.targets || []).map((t) => t.class_id),
      section_ids: (row.targets || [])
        .map((t) => t.section_id)
        .filter(Boolean),
    });
    setFile(null);
    setOpen(true);
  };

  const save = async () => {
    const title = (form.title || "").trim();
    if (!title) {
      toast.error("Title is required");
      return;
    }
    setSaving(true);
    try {
      let attachmentUrl = editing?.attachment_url;
      if (file) {
        const fd = new FormData();
        fd.append("file", file, file.name);
        const { data } = await api.post(
          `/upload?tag=${testid}`,
          fd,
          { headers: { "Content-Type": "multipart/form-data" } },
        );
        attachmentUrl = data.url;
      }

      const body = {
        title,
        content: form.content || "",
        date: form.date || "",
        all_classes: !!form.all_classes,
        class_ids: form.all_classes ? [] : form.class_ids || [],
        section_ids: form.all_classes ? [] : form.section_ids || [],
        branch_id: branchId || "",
        attachment_url: attachmentUrl || "",
        ...(hasAudience ? { audience: form.audience || "staff" } : {}),
      };

      if (editing) {
        await api.put(`${endpoint}/${editing.id}`, body);
      } else {
        await api.post(endpoint, body);
      }
      toast.success(editing ? "Updated" : "Circular published");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const del = async () => {
    if (!deleteRow?.id) return;
    await api.delete(`${endpoint}/${deleteRow.id}`);
    toast.success("Deleted");
    setDeleteRow(null);
    load();
  };

  return (
    <div data-testid={`${testid}-page`}>
      <PageTitle
        title={title}
        actions={
          permissions.canAdd ? (
            <Button
              size="sm"
              onClick={openCreate}
              className="h-8 erp-primary"
              data-testid={`${testid}-add`}
            >
              <Plus className="w-3.5 h-3.5 mr-1" /> New {title}
            </Button>
          ) : null
        }
      />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editing ? "Edit" : "New"} {title}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs font-semibold mb-1 block">
                Title *
              </Label>
              <Input
                value={form.title || ""}
                onChange={(e) =>
                  setForm((f) => ({ ...f, title: e.target.value }))
                }
                className="h-9 text-sm"
                data-testid={`${testid}-title`}
              />
            </div>
            {hasAudience && (
              <div>
                <Label className="text-xs font-semibold mb-1 block">
                  Available For
                </Label>
                <select
                  className="h-9 w-full rounded-md border border-slate-300 text-sm px-2 bg-white"
                  value={form.audience || "staff"}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, audience: e.target.value }))
                  }
                  data-testid={`${testid}-audience`}
                >
                  {CIRCULAR_AUDIENCE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <ClassSectionTargeting
              form={form}
              setForm={setForm}
              classes={classes}
              sections={sections}
            />
            <div>
              <Label className="text-xs font-semibold mb-1 block">Date</Label>
              <DatePicker
                value={form.date || ""}
                onChange={(v) => setForm((f) => ({ ...f, date: v }))}
                className="h-9 text-sm w-full"
                testId={`${testid}-date`}
              />
            </div>
            <div>
              <Label className="text-xs font-semibold mb-1 block">
                Message
              </Label>
              <textarea
                className="w-full rounded-md border border-slate-300 text-sm px-2 py-1.5 min-h-[90px]"
                value={form.content || ""}
                onChange={(e) =>
                  setForm((f) => ({ ...f, content: e.target.value }))
                }
                data-testid={`${testid}-content`}
              />
            </div>
            <div>
              <Label className="text-xs font-semibold mb-1 block">
                Attachment (optional)
                {editing?.attachment_url && (
                  <span className="font-normal text-slate-400">
                    {" "}
                    (leave blank to keep existing file)
                  </span>
                )}
              </Label>
              <input
                type="file"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="text-xs"
                data-testid={`${testid}-file`}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              className="erp-primary"
              onClick={save}
              disabled={saving}
              data-testid={`${testid}-save`}
            >
              {editing ? "Update" : "Publish"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div>
          <div className="flex flex-wrap gap-2 mb-3">
            <select
              className="h-8 rounded-md border border-slate-300 text-xs px-2 bg-white"
              value={filterClass}
              onChange={(e) => setFilterClass(e.target.value)}
              data-testid={`filter-${testid}-class`}
            >
              <option value="">All Classes</option>
              {classes.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                placeholder="Search title / message…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="h-8 pl-7 w-56 text-xs"
                data-testid={`search-${testid}`}
              />
            </div>
          </div>

          <DataTable
            testid={`${testid}-table`}
            columns={[
              { key: "created_by_name", label: "Created By" },
              { key: "title", label: "Title" },
              {
                key: "content",
                label: "Message",
                render: (r) => (
                  <span className="line-clamp-2 max-w-xs block text-xs">
                    {r.content}
                  </span>
                ),
              },
              { key: "date", label: "Date" },
              ...(hasAudience
                ? [{ key: "audience", label: "Available For" }]
                : []),
              { key: "class_summary", label: "Class" },
              {
                key: "actions",
                label: "Action",
                render: (r) => (
                  <div className="flex items-center gap-1 justify-end">
                    {r.attachment_url && (
                      <a
                        href={fileUrlFor(r.attachment_url)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-1.5 rounded hover:bg-blue-50 erp-text-primary"
                        title="View attachment"
                        data-testid={`${testid}-view-${r.id}`}
                      >
                        <Eye className="w-3.5 h-3.5" />
                      </a>
                    )}
                    {permissions.canEdit && (
                      <button
                        className="p-1.5 rounded hover:bg-blue-50 erp-text-primary"
                        onClick={() => openEdit(r)}
                        title="Edit"
                        data-testid={`${testid}-edit-${r.id}`}
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                    )}
                    {permissions.canDelete && (
                      <button
                        className="p-1.5 rounded hover:bg-red-50 text-red-600"
                        onClick={() => setDeleteRow(r)}
                        title="Delete"
                        data-testid={`${testid}-delete-${r.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                ),
              },
            ]}
            rows={rows}
          />
      </div>

      <AlertDialog
        open={!!deleteRow}
        onOpenChange={(v) => !v && setDeleteRow(null)}
      >
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete "{deleteRow?.title}"?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove this circular.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={del}
              className="bg-rose-600 hover:bg-rose-700 text-white"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function CircularsPage() {
  return (
    <CircularsLikePage
      title="Circulars"
      endpoint="/circulars"
      testid="circulars"
      permissionKey="circulars"
      hasAudience
    />
  );
}

function StudentCircularsPage() {
  return (
    <CircularsLikePage
      title="Student Circulars"
      endpoint="/student-circulars"
      testid="stu-circ"
      permissionKey="student_circulars"
      hasAudience={false}
    />
  );
}

function resolveChatAttachmentUrl(path) {
  const value = String(path || "").trim();
  if (!value) return "";
  if (/^https?:\/\//i.test(value)) return value;
  const normalized = value.startsWith("/") ? value : `/${value}`;
  return BACKEND_URL ? `${BACKEND_URL}${normalized}` : normalized;
}

function ChatMessageBubble({ msg }) {
  const isTeacher = msg.sender_type === "teacher";
  return (
    <div className={`flex mb-2 ${isTeacher ? "justify-end" : "justify-start"}`}>
      <div
        className={`max-w-[75%] rounded-xl px-3 py-2 text-sm ${
          isTeacher
            ? "bg-indigo-600 text-white"
            : "bg-white border border-slate-200 text-slate-800"
        }`}
      >
        {msg.message && <div className="whitespace-pre-wrap">{msg.message}</div>}
        {msg.attachment_url && (
          <a
            href={resolveChatAttachmentUrl(msg.attachment_url)}
            target="_blank"
            rel="noreferrer"
            className={`flex items-center gap-1 text-xs mt-1 underline ${
              isTeacher ? "text-white" : "text-indigo-700"
            }`}
          >
            <Download className="w-3 h-3" /> {msg.attachment_name || "Attachment"}
          </a>
        )}
        <div className={`text-[10px] mt-1 ${isTeacher ? "text-indigo-100" : "text-slate-400"}`}>
          {isTeacher ? "Teacher" : "Student"} ·{" "}
          {String(msg.created_at || "").replace("T", " ").slice(0, 16)}
        </div>
      </div>
    </div>
  );
}

// Read-only view for admins of every student <-> class-teacher chat thread —
// admin never sends here, just monitors (see ChatController::messages, which
// only marks messages read for viewer=student/teacher, never for admin).
export function ClassTeacherChatsPage() {
  const { branchId, sessionId } = useAuth();
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [openThread, setOpenThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [msgLoading, setMsgLoading] = useState(false);

  const loadThreads = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (branchId) params.branch_id = branchId;
      if (sessionId) params.session_id = sessionId;
      const { data } = await api.get("/chat/threads", { params });
      setThreads(data.items || []);
    } catch (e) {
      toast.error("Failed to load chats");
    } finally {
      setLoading(false);
    }
  }, [branchId, sessionId]);

  useEffect(() => {
    loadThreads();
  }, [loadThreads]);

  const openChat = async (thread) => {
    setOpenThread(thread);
    setMsgLoading(true);
    try {
      const { data } = await api.get("/chat/messages", {
        params: { student_id: thread.student_id, teacher_id: thread.teacher_id },
      });
      setMessages(data.items || []);
    } catch (e) {
      toast.error("Failed to load messages");
    } finally {
      setMsgLoading(false);
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return threads;
    return threads.filter((t) =>
      [t.student_name, t.teacher_name, t.class_name, t.last_message]
        .join(" ")
        .toLowerCase()
        .includes(q),
    );
  }, [threads, search]);

  return (
    <div data-testid="chats-page">
      <PageTitle
        title="Student ↔ Class Teacher Chats"
        subtitle="Read-only view of every chat thread across the school"
      />
      <div className="mb-3 relative max-w-sm">
        <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
        <Input
          placeholder="Search student, teacher, class..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-8"
          data-testid="chats-search"
        />
      </div>
      <DataTable
        testid="chats-table"
        emptyLabel={loading ? "Loading..." : "No chat activity yet"}
        rows={filtered}
        columns={[
          {
            key: "student_name",
            label: "Student",
            render: (r) => (
              <div>
                <div className="font-medium">{r.student_name}</div>
                <div className="text-[11px] text-slate-400">{r.admission_no}</div>
              </div>
            ),
          },
          { key: "class_name", label: "Class" },
          { key: "teacher_name", label: "Class Teacher" },
          {
            key: "last_message",
            label: "Last Message",
            render: (r) =>
              r.last_message ? (
                <span className="text-slate-600">
                  {r.last_message.length > 50
                    ? `${r.last_message.slice(0, 50)}…`
                    : r.last_message}
                </span>
              ) : (
                <span className="text-slate-300 italic">No messages yet</span>
              ),
          },
          {
            key: "last_message_at",
            label: "Last Activity",
            render: (r) =>
              r.last_message_at ? (
                r.last_message_at.replace("T", " ").slice(0, 16)
              ) : (
                <span className="text-slate-300">—</span>
              ),
          },
          {
            key: "unread",
            label: "Unread",
            render: (r) => {
              const total =
                (Number(r.unread_from_student) || 0) +
                (Number(r.unread_from_teacher) || 0);
              return total > 0 ? (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-rose-100 text-rose-700 text-xs font-semibold">
                  {total}
                </span>
              ) : (
                <span className="text-slate-300">—</span>
              );
            },
          },
          {
            key: "open",
            label: "",
            render: (r) => (
              <Button
                size="sm"
                variant="outline"
                onClick={() => openChat(r)}
                className="gap-1"
                data-testid={`chat-view-${r.student_id}`}
              >
                <Eye className="w-3.5 h-3.5" /> View
              </Button>
            ),
          },
        ]}
      />

      <Dialog
        open={!!openThread}
        onOpenChange={(v) => {
          if (!v) {
            setOpenThread(null);
            setMessages([]);
          }
        }}
      >
        <DialogContent className="max-w-lg max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>
              {openThread?.student_name} ↔ {openThread?.teacher_name}
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto px-1 py-2 bg-slate-50 rounded-md">
            {msgLoading ? (
              <div className="text-center text-xs text-slate-400 py-10">Loading…</div>
            ) : messages.length === 0 ? (
              <div className="text-center text-xs text-slate-400 py-10">No messages yet</div>
            ) : (
              messages.map((m) => <ChatMessageBubble key={m.id} msg={m} />)
            )}
          </div>
          <div className="text-[11px] text-slate-400 pt-2">
            Read-only admin view — messages are between the student and their class
            teacher.
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function CommunicatePage() {
  return (
    <div data-testid="communicate-page">
      <Sub
        prefix="cm"
        items={[
          { to: "/communicate/circulars", label: "Circulars" },
          { to: "/communicate/student-circulars", label: "Student Circulars" },
          { to: "/communicate/chats", label: "Chats" },
          { to: "/communicate/helpdesk", label: "Helpdesk" },
          { to: "/communicate/downloads", label: "Download Center" },
        ]}
      />
      <Routes>
        <Route index element={<Navigate to="/communicate/circulars" replace />} />
        <Route path="circulars" element={<CircularsPage />} />
        <Route path="student-circulars" element={<StudentCircularsPage />} />
        <Route path="chats" element={<ClassTeacherChatsPage />} />
        <Route
          path="helpdesk"
          element={
            <MasterCrudPage
              title="Helpdesk Tickets"
              endpoint="/helpdesk-tickets"
              testid="helpdesk"
              fields={[
                { key: "subject", label: "Subject *", required: true },
                { key: "student_name", label: "Student" },
                { key: "class", label: "Class" },
                { key: "support_staff", label: "Assigned Staff" },
                {
                  key: "status",
                  label: "Status",
                  type: "select",
                  options: [
                    { value: "open", label: "Open" },
                    { value: "ongoing", label: "Ongoing" },
                    { value: "resolved", label: "Resolved" },
                  ],
                },
                {
                  key: "message",
                  label: "Message",
                  type: "textarea",
                  full: true,
                },
              ]}
            />
          }
        />
        <Route
          path="downloads"
          element={<DownloadCenterPage />}
        />
      </Routes>
    </div>
  );
}
